// Studio overview (contracts/studio.md §4): the engine status line, Claude usage against the monthly
// budget with a per-model breakdown, the AI kill switch, the review queue and the latest jobs.

import { Link, useNavigate } from "react-router";
import { useReviewQueue, useStudioJobs } from "../../api/studio";
import { useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, Card, EmptyState, PageHeader } from "../../components/ui";
import { isActiveJob } from "../../lib/studio";
import { EngineStatus, KillSwitch, RoleNote, StudioError, UsageCard } from "./components";
import { JobsTable, LiveBadge } from "./jobs";
import s from "./studio.module.css";

export function StudioHomePage() {
  const canEdit = useCan("studio.edit");
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        title="Studio"
        meta={<span>Generate puzzles, review drafts, and schedule the bank. AI never decides correctness: solver types must pass uniqueness; the rest need a person.</span>}
        actions={
          <>
            <RoleNote />
            <Button variant="ghost" onClick={() => navigate("/studio/bank")}>
              Bank
            </Button>
            {canEdit ? (
              <Button variant="primary" onClick={() => navigate("/studio/new")}>
                New job
              </Button>
            ) : null}
          </>
        }
      />
      <EngineStatus />
      <div className={s.homeGrid}>
        <div className={s.stack}>
          <UsageCard />
          <RecentJobs />
        </div>
        <div className={s.stack}>
          <KillSwitch />
          <ReviewQueue />
        </div>
      </div>
    </>
  );
}

function RecentJobs() {
  const q = useStudioJobs(null, null, 6);
  const live = Boolean(q.data?.items.some((j) => isActiveJob(j.status)));
  return (
    <Card
      title="Latest jobs"
      padded={false}
      actions={
        <>
          <LiveBadge live={live} />
          <Link to="/studio/jobs" className={tableStyles.id}>
            All jobs →
          </Link>
        </>
      }
    >
      {q.isError ? (
        <div className={s.padded}>
          <StudioError error={q.error} onRetry={() => void q.refetch()} />
        </div>
      ) : q.data && q.data.items.length === 0 ? (
        <EmptyState title="No jobs yet">Queue a procedural or Claude job to fill the bank.</EmptyState>
      ) : (
        <div className={s.flushTable}>
          <JobsTable jobs={q.data?.items} loading={q.isPending} rows={4} compact />
        </div>
      )}
    </Card>
  );
}

function ReviewQueue() {
  const q = useReviewQueue();
  const n = q.data?.items.length ?? 0;
  const capped = Boolean(q.data?.nextCursor);
  return (
    <Card title="Waiting for review">
      <div className={s.switchRow} data-align="center">
        <span className={s.bigCount} data-testid="review-count">
          {q.isPending ? "…" : `${n}${capped ? "+" : ""}`}
        </span>
        <p className={s.notes}>Bank puzzles without a solver. Approving one is its review; only then can it be scheduled.</p>
      </div>
      <div className={s.cardFoot}>
        <Link to="/studio/bank?review=pending" className={tableStyles.id}>
          Review in the bank →
        </Link>
      </div>
      {q.isError ? <StudioError error={q.error} /> : null}
    </Card>
  );
}
